import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';
import useEditorStore from '../stores/useEditorStore';
import useWorkflowStore from '../stores/useWorkflowStore';
import { loadMeshFromSourceFiles } from '../lib/bkScanSourceFiles';
import { getPendingSourceMeshAutoLoad } from '../lib/scanMeshLoadGate';
import { getStepFromPathname } from '../constants/flowConfig';
import { loadBkDesignOrderContext } from '../lib/bkDesignOrderContext';
import { hasCachedStep } from '../lib/bkSceneCache';

/**
 * When scan file metadata exists (get_bk_design_files_meta) and the mesh has not
 * been loaded yet, download the best mesh (STL > PLY > OBJ) into the editor.
 * OBJ loads with MTL + images from the same source_files list when present.
 * Use only under /design — not on the dashboard (no 3D preview there).
 *
 * @param {(info: { fileName: string, fileType: string }) => void} [onAutoLoaded]
 */
export function useBkScanAutoLoad(onAutoLoaded) {
  const onAutoLoadedRef = useRef(onAutoLoaded);
  onAutoLoadedRef.current = onAutoLoaded;
  const location = useLocation();

  const [scanFilesMeta, scanAutoLoaded, setScanAutoLoaded] = useWorkflowStore(
    useShallow((s) => [s.scanFilesMeta, s.scanAutoLoaded, s.actions.setScanAutoLoaded])
  );
  const setUploadedModelData = useEditorStore((s) => s.setUploadedModelData);
  const uploadedModelData = useEditorStore((s) => s.uploadedModelData);

  useEffect(() => {
    if (!scanFilesMeta || scanAutoLoaded) return;
    // Never replace a mesh the user picked on the dashboard in this session.
    if (uploadedModelData?.source === 'local_upload') return;
    const path = location.pathname;

    // A cached (edited) scene for this step is about to be restored from the
    // client cache — don't reload the raw scan over it.
    const ctx = loadBkDesignOrderContext();
    const cachedStep = ctx && getStepFromPathname(path);
    if (cachedStep && hasCachedStep(`${ctx.sales_order}:${ctx.customer}`, cachedStep)) {
      setScanAutoLoaded(true);
      return;
    }

    const primary = getPendingSourceMeshAutoLoad(path, scanFilesMeta);

    // Checkpoint / finalization routes, or only non-mesh sources (e.g. photos).
    if (!primary) {
      setScanAutoLoaded(true);
      return;
    }

    const ac = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        const { type, data } = await loadMeshFromSourceFiles(scanFilesMeta.source_files, ac.signal);
        if (cancelled || ac.signal.aborted) return;
        setUploadedModelData({ type, data, source: 'server_source' });
        setScanAutoLoaded(true);
        onAutoLoadedRef.current?.({ fileName: primary.name, fileType: type });
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.error('BK scan auto-load:', err);
        setScanAutoLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [
    scanFilesMeta,
    scanAutoLoaded,
    setUploadedModelData,
    setScanAutoLoaded,
    location.pathname,
    uploadedModelData?.source,
  ]);
}
